import { createRouter, createWebHistory } from 'vue-router'
import { useStorage } from '@vueuse/core'
import { usePopupStore } from '@/stores/popup'
import { useMenuStore } from '@/stores/menu'
import { DRAFT_LISTING_ROUTES, useDraftsStore } from '@/stores/drafts'
import { useSearchStore } from '@/stores/search'
import { useAgencyStore } from '@/stores/agency'
import { useUpgradeStore } from '@/stores/upgrade'
import { isProduction } from '@/helpers/envCheck'
import { mobileCheck } from '@/helpers/mobileCheck'
import { checkWgUrlAccess } from '@/helpers/wgUrlAccess'
import { isAgencyRouteAllowed } from '@/helpers/agencyRoutes'
import { canCreatePresentations, isPresentationCreationPath } from '@/helpers/presentationAccess'
import { requiredPlanForBookFormat } from '@/helpers/product'
import type { TUser, TStorageUser, TStorageAccountType } from '@/types/user'
import type { RouteLocationNormalized } from 'vue-router'
import HomeView from '@/views/HomeView.vue'
import DashboardView from '@/views/agency/client/DashboardView.vue'

const accountType = useStorage('accountType', {} as TStorageAccountType)

const alwaysAllowed = [
  '/dashboard/mobile-support',
  '/dashboard/login',
  '/dashboard/forgot',
  '/dashboard/reset',
  '/dashboard/setup_password',
  '/dashboard/request-credentials',
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  scrollBehavior(to) {
    if (to.hash) {
      return { el: to.hash, behavior: 'smooth' }
    }
    return { top: 0, behavior: 'instant' }
  },
  routes: [
    {
      path: '/',
      name: 'home',
      component: HomeView,
    },
    {
      path: '/projects/folders',
      name: 'projects/folders',
      component: () => import('@/views/projects/folders/IndexView.vue'),
    },
    {
      path: '/projects/:type?',
      name: 'projects',
      component: () => import('@/views/projects/IndexView.vue'),
    },
    {
      path: '/projects/folders/:id',
      name: 'projects/folders/folder',
      component: () => import('@/views/projects/folders/FolderView.vue'),
    },
    {
      path: '/media',
      name: 'media/index',
      component: () => import('@/views/media/IndexView.vue'),
    },
    {
      path: '/media/my-uploads',
      name: 'media/my-uploads',
      component: () => import('@/views/media/MyUploadsView.vue'),
    },
    {
      path: '/media/collections',
      name: 'media/collections/index',
      component: () => import('@/views/media/collections/IndexView.vue'),
    },
    {
      path: '/media/collections/:id',
      name: 'media/collections/collection',
      component: () => import('@/views/media/collections/CollectionView.vue'),
    },
    {
      path: '/media/favorites',
      name: 'media-favorites',
      component: () => import('@/views/media/FavoritesView.vue'),
    },
    {
      path: '/creator/:type?/:import?',
      name: 'creator',
      component: () => import('@/views/creator/IndexView.vue'),
    },
    {
      path: '/creator/wordgenie/:type?',
      name: 'wordgenie',
      component: () => import('@/views/creator/WordgenieView.vue'),
    },
    {
      path: '/wordgenie-pro/:type(book|presentation)?',
      name: 'wordgenie-pro',
      component: () => import('@/views/wordgenie-pro/IndexView.vue'),
    },
    {
      path: '/my-templates',
      name: 'my-templates',
      component: () => import('@/views/templates/MyTemplates.vue'),
    },
    {
      path: '/creator/templates',
      name: 'templates',
      component: () => import('@/views/creator/TemplatesView.vue'),
    },
    {
      path: '/creator/presentation-outline',
      name: 'presentation-outline',
      component: () => import('@/views/creator/PresentationOutlineView.vue'),
    },
    {
      path: '/creator/presentation-themes',
      name: 'presentation-themes',
      component: () => import('@/views/creator/PresentationThemesView.vue'),
    },
    {
      path: '/creator/review',
      name: 'review',
      component: () => import('@/views/creator/ReviewView.vue'),
    },
    {
      path: '/creator/publish/:slugId',
      name: 'publish',
      component: () => import('@/views/creator/PublishView.vue'),
    },
    {
      path: '/docs/transcription/:id',
      name: 'draft-transcription',
      // component: () => import('@/views/legacy/IndexView.vue'),
      component: () => import('@/views/drafts/edit/TranscriptionView.vue'),
    },
    {
      path: '/docs/audiobook/:id',
      name: 'draft-audiobook',
      component: () => import('@/views/drafts/edit/AudiobookView.vue'),
    },
    {
      path: '/docs/ebook/:id',
      name: 'draft-ebook',
      component: () => import('@/views/drafts/edit/EditEBookView.vue'),
    },
    {
      path: '/tiptapdemo',
      name: 'tiptap-demo',
      component: () => import('@/views/drafts/edit/TipTapDemoView.vue'),
    },
    {
      path: '/docs/print-book/:id',
      name: 'draft-print-book',
      component: () => import('@/views/drafts/edit/EditEBookView.vue'),
    },
    {
      path: '/docs/print-book-pdf/:id/:token?',
      name: 'draft-print-book-pdf',
      component: () => import('@/views/drafts/PrintBookPdf.vue'),
    },
    {
      path: '/docs/kindle-book/:id',
      name: 'draft-kindle-book',
      component: () => import('@/views/drafts/edit/EditEBookView.vue'),
    },
    {
      path: '/docs/:type?',
      name: 'drafts',
      component: () => import('@/views/drafts/IndexView.vue'),
    },
    {
      path: '/search/:query?',
      name: 'search',
      component: () => import('@/views/search/IndexView.vue'),
    },
    {
      path: '/questionnaire',
      name: 'questionnaire',
      component: () => import('@/views/questionnaire/IndexView.vue'),
    },
    {
      path: '/profile/:tab?',
      name: 'profile',
      component: () => import('@/views/profile/IndexView.vue'),
    },
    {
      path: '/agency',
      name: 'agency',
      component: () => import('@/views/agency/IndexView.vue'),
    },
    {
      path: '/team',
      name: 'team',
      component: () => import('@/views/agency/team/IndexView.vue'),
    },
    {
      path: '/team/:id/:slug?',
      name: 'team-member',
      component: () => import('@/views/agency/team/MemberView.vue'),
    },
    {
      path: '/clients',
      name: 'clients',
      component: () => import('@/views/agency/client/IndexView.vue'),
    },
    {
      path: '/clients/:id/:slug?',
      name: 'clients-detail',
      component: () => import('@/views/agency/client/ClientView.vue'),
    },
    {
      path: '/clients/no_access',
      name: 'client_no_access',
      component: () => import('@/views/login/ClientNoAccess.vue'),
    },
    {
      path: '/admin/suggestions',
      name: 'adminSuggestions',
      component: () => import('@/views/admin/SuggestionsView.vue'),
    },
    {
      path: '/admin/variant-niche-images',
      name: 'variantNicheImages',
      component: () => import('@/views/admin/VariantNicheImages.vue'),
    },
    {
      path: '/admin/refund-bot',
      name: 'adminRefundBot',
      component: () => import('@/views/admin/refund-bot/RefundBotView.vue'),
    },
    {
      path: '/admin/refund-bot/excluded/:id?',
      name: 'adminRefundBotExcludedProducts',
      component: () => import('@/views/admin/refund-bot/RefundBotExcludedView.vue'),
    },
    {
      path: '/admin/refund-bot/group/:id',
      name: 'adminRefundBotProducts',
      component: () => import('@/views/admin/refund-bot/RefundBotProductsView.vue'),
    },
    {
      path: '/admin/ai-prompts',
      name: 'adminRefundPrompts',
      component: () => import('@/views/admin/ai-prompts/AIPromptsView.vue'),
    },
    {
      path: '/admin/stripe-prices',
      name: 'adminStripePrices',
      component: () => import('@/views/admin/stripe-prices/StripePricesView.vue'),
    },
    {
      path: '/admin/ai-edit-suggestions',
      name: 'adminAIEditSuggestions',
      component: () => import('@/views/admin/ai-edit-suggestions/AIEditSuggestionsView.vue'),
    },
    {
      path: '/admin/image-themes',
      name: 'image-themes',
      component: () => import('@/views/admin/ImageForThemes.vue'),
    },
    {
      path: '/admin/themes-management',
      name: 'themes-management',
      component: () => import('@/views/admin/ThemesManagement.vue'),
    },
    {
      path: '/admin/templates-themes',
      name: 'templates-themes',
      component: () => import('@/views/admin/TemplatesThemes.vue'),
    },
    {
      path: '/admin/:module?',
      name: 'admin',
      component: () => import('@/views/legacy/IndexView.vue'),
    },
    {
      path: '/admin/users',
      name: 'admin-users',
      component: () => import('@/views/admin/IndexView.vue'),
    },
    {
      path: '/admin/default-templates-projects',
      name: 'default-templates-projects',
      component: () => import('@/views/admin/DefaultTemplatesProjects.vue'),
    },
    {
      path: '/admin/audio-transcript-reports',
      name: 'audio-transcript-reports',
      component: () => import('@/views/admin/AudioTranscriptReports.vue'),
    },
    {
      path: '/admin/url-import-statistics',
      name: 'url-import-statistics',
      component: () => import('@/views/admin/UrlImportStatistics.vue'),
    },
    {
      path: '/admin/statistics',
      name: 'admin-statistics',
      component: () => import('@/views/admin/StatisticsView.vue'),
    },
    {
      path: '/admin/tips',
      name: 'admin-tips',
      component: () => import('@/views/admin/TipsView.vue'),
    },
    {
      path: '/admin/top-bars',
      name: 'admin-top-bars',
      component: () => import('@/views/admin/TopBarsView.vue'),
    },
    {
      path: '/admin/tours',
      name: 'admin-tours',
      component: () => import('@/views/admin/ToursView.vue'),
    },
    {
      path: '/ask-for-a-refund/:type',
      name: 'ask-for-a-refund',
      component: () => import('@/views/legacy/IndexView.vue'),
    },
    {
      path: '/login',
      name: 'login',
      component: () => import('@/views/login/LoginView.vue'),
    },
    {
      path: '/forgot',
      name: 'forgot',
      component: () => import('@/views/login/ForgotPasswordView.vue'),
    },
    {
      path: '/reset',
      name: 'reset',
      component: () => import('@/views/login/ResetPasswordView.vue'),
    },
    {
      path: '/request-credentials',
      name: 'request-credentials',
      component: () => import('@/views/login/RequestCredentialsView.vue'),
    },
    {
      path: '/setup_password',
      name: 'setup_password',
      component: () => import('@/views/login/SetupPasswordView.vue'),
    },
    {
      path: '/mobile-support',
      name: 'mobile-support',
      component: () => import('@/views/error/MobileView.vue'),
    },
    {
      path: '/:catchAll(.*)',
      name: '404',
      component: () => import('@/views/error/404View.vue'),
    },
    {
      path: '/client',
      name: 'client_dashboard',
      component: DashboardView,
    },
    {
      path: '/client/account',
      name: 'clients_account',
      component: () => import('@/views/agency/client/ClientSettingsView.vue'),
    },
    {
      path: '/cover-editor/:id',
      name: 'cover-editor',
      component: () => import('@/views/print-book/CoverEditorView.vue'),
    },
    {
      path: '/presentation-editor/:id',
      name: 'presentation-editor',
      component: () => import('@/views/presentation/EditorView.vue'),
    },
    {
      path: '/ebook-editor/:id',
      name: 'ebook-editor',
      component: () => import('@/views/ebook-editor/EbookEditorView.vue'),
    },
    {
      path: '/ebook-render/:id/:token',
      name: 'ebook-render',
      component: () => import('@/views/ebook-editor/EbookRenderView.vue'),
    },
    {
      path: '/presentation-render/:id/:index/:token',
      name: 'presentation-render',
      component: () => import('@/views/presentation/PresentationRenderView.vue'),
    },
    {
      path: '/presentation-render-batch/:id/:token',
      name: 'presentation-render-batch',
      component: () => import('@/views/presentation/PresentationRenderBatchView.vue'),
    },
    {
      path: '/presentation-flipbook/:id/:token',
      name: 'presentation-flipbook',
      component: () => import('@/views/presentation/PresentationFlipbookView.vue'),
    },
    {
      path: '/presentation-share/:hash',
      name: 'presentation-share',
      component: () => import('@/views/presentation/PresentationShareView.vue'),
    },
    {
      path: '/presentation-share/:id/:token',
      name: 'presentation-share-short',
      component: () => import('@/views/presentation/PresentationShareView.vue'),
    },
    {
      path: '/cover-editor/preview/thumbnail/:id/:dpi/:token',
      name: 'cover-editor-preview-thumbnail',
      component: () => import('@/views/print-book/CoverPreviewView.vue'),
    },
    {
      path: '/cover-editor/preview/:id/:dpi/:token',
      name: 'cover-editor-preview',
      component: () => import('@/views/print-book/CoverPreviewView.vue'),
    },
    {
      path: '/cover-editor/print-book-preview/:id',
      name: 'print-book-preview',
      component: () => import('@/views/print-book/PrintPreviewView.vue'),
    },
    {
      path: '/cover-editor/kindle-book-preview/:id',
      name: 'kindle-book-preview',
      component: () => import('@/views/kindle-book/KindlePreviewView.vue'),
    },
    {
      path: '/cover-editor/print-book-download/:id',
      name: 'print-book-download',
      component: () => import('@/views/print-book/PrintDownloadView.vue'),
    },
    {
      path: '/cover-editor/kindle-book-download/:id',
      name: 'kindle-book-download',
      component: () => import('@/views/kindle-book/KindleDownloadView.vue'),
    },
    {
      path: '/fish-audio-test',
      name: 'fish-audio-test',
      component: () => import('@/views/fish-audio-test/FishAudioTestView.vue'),
    },
  ],
})

router.afterEach(() => {
  if (mobileCheck() && isProduction() && !alwaysAllowed.includes(location.pathname)) {
    location.href = '/dashboard/mobile-support'
  }
})

router.afterEach((to: RouteLocationNormalized, from: RouteLocationNormalized) => {
  if (DRAFT_LISTING_ROUTES.includes(String(from.name)) && !DRAFT_LISTING_ROUTES.includes(String(to.name))) {
    useDraftsStore().invalidateAll()
  }
})

// off the results page the listing stores go back to being listings, so they stop being results
router.afterEach((to: RouteLocationNormalized, from: RouteLocationNormalized) => {
  if (from.name === 'search' && to.name !== 'search') {
    useSearchStore().clearListingResults()
  }
})

const printBookPaths = [
  '/docs/print-book/',
  '/cover-editor/print-book-preview/',
  '/cover-editor/print-book-download/',
  '/creator/printbook',
]

const kindleBookPaths = [
  '/docs/kindle-book/',
  '/cover-editor/kindle-book-preview/',
  '/cover-editor/kindle-book-download/',
]

function handleCustomRedirects(to: RouteLocationNormalized, user: TUser): string | null {
  const p = user?.permissions
  const acc = accountType.value?.value
  const agency = useAgencyStore()
  const rules = [
    {
      condition: () => to.path === '/my-templates' && !(p?.['superuser'] || p?.['templates.editor']),
      redirect: '/',
    },
    {
      condition: () => (to.path.startsWith('/ebook-editor') || to.path.startsWith('/ebook-render')) && isProduction(),
      redirect: '/',
    },
    {
      condition: () => printBookPaths.some((path) => to.path.startsWith(path)) && !(acc?.printbook > 0),
      redirect: '/',
    },
    {
      condition: () => kindleBookPaths.some((path) => to.path.startsWith(path)) && !(acc?.ebook_export > 0),
      redirect: '/',
    },
    {
      condition: () => to.path.startsWith('/wordgenie-pro') && !checkWgUrlAccess('/wordgenie-pro', user, acc).allowed,
      redirect: '/creator/wordgenie',
    },
    {
      condition: () => !isAgencyRouteAllowed(to.path, agency.canManageUsers, agency.canManageClients),
      redirect: '/',
    },
    {
      condition: () => isPresentationCreationPath(to.path) && !canCreatePresentations(user, acc),
      redirect: '/',
      onRedirect: () => useUpgradeStore().showUpgradeModal('presentation-create', requiredPlanForBookFormat('presentation')),
    },
  ]

  for (const rule of rules) {
    if (rule.condition()) {
      rule.onRedirect?.()

      return rule.redirect
    }
  }

  return null
}

router.beforeEach((to: RouteLocationNormalized, from: RouteLocationNormalized) => {
  const creatorPaths = [
    '/creator',
    '/docs/ebook/',
    '/docs/kindle-book/',
    '/docs/print-book/',
    '/docs/audiobook/',
    '/docs/transcription/',
    '/cover-editor/',
    '/presentation-editor/',
    '/ebook-editor/',
    '/wordgenie-pro',
  ]
  const fromCreator = creatorPaths.some((path) => from.fullPath.includes(path))
  const toCreator = creatorPaths.some((path) => to.fullPath.includes(path))

  if (fromCreator && !toCreator) {
    const menu = useMenuStore()
    menu.expandMenuIwWasAutomaticallyHidden()
  } else if (!fromCreator && toCreator) {
    const menu = useMenuStore()
    menu.closeMenuAutomaticallyIfOpened()
  }

  const user: TUser = useStorage('user', {} as TStorageUser).value.value
  const allowed = [
    ...alwaysAllowed,
    '/cover-editor/3d-preview',
    '/cover-editor/preview',
    '/docs/print-book-pdf',
    '/ebook-render',
    '/presentation-render',
    '/presentation-render-batch',
    '/presentation-flipbook',
    '/presentation-share',
  ]
  if (!user) {
    const found = allowed.find((r) => window.location.href.includes(r))
    if (!found) {
      window.location.href = '/dashboard/login'
      return false
    }
  }

  if (accountType.value?.value?.agencyClient) {
    const clientAllowed = ['/client', '/404', '/login']
    const allowedForClient = clientAllowed.some((p) => to.path === p || to.path.startsWith(p + '/'))
    if (!allowedForClient) {
      return '/404'
    }
  }

  const redirectPath = handleCustomRedirects(to, user)
  if (redirectPath) {
    return redirectPath
  }
})

router.onError(async () => {
  const popup = usePopupStore()
  const result = await popup.confirm('New version available', {
    labelCancel: 'Cancel',
    labelConfirm: 'Refresh',
    content: 'It seems that there is a new version, please refresh this page.',
  })
  if (!result) {
    return
  }
  window.location.reload()
})

export default router
